<script setup lang="ts">
import type { CmsElementSidebarFilter } from '@shopware/composables'
import type { RequestParameters, Schemas } from '#shopware'
import type { RouteLocationRaw } from 'vue-router'
import { getTranslatedProperty, type getListingFilters } from '@shopware/helpers'
import {
    ProductListingFilterPrice,
    ProductListingFilterProperties,
} from '#components'

type ListingFilter = ReturnType<typeof getListingFilters>[number]

const props = defineProps<{
    content?: CmsElementSidebarFilter
    listingType?: 'category' | 'search'
}>()

const { public: { isNewPropertyId: IS_NEW_PROPERTY_ID } } = useRuntimeConfig()

const route = useRoute()
const router = useRouter()

const { t, locale, defaultLocale } = useI18n()

const isNewPage = computed(() => {
    let path = route.path
    if (locale.value !== defaultLocale) {
        path = path.replace(new RegExp(`^/${locale.value}`), '') || '/'
    }
    return path === '/new' || path.startsWith('/new/')
})

const {
    isSearchListing,
    getInitialFilters,
    getCurrentSortingOrder,
    getCurrentListing,
    filtersToQuery,
    doSearch,
} = useListingContext(props.listingType ?? 'category')

const { withOnSalePostFilter, isOnSalePage } = useOnSaleFilter()

interface CategoryItem {
    afterId: string
    category: {
        translated: { name: string }
        active: boolean
        id: string
        seoUrl: string
    }
    children: Record<string, CategoryItem>
}

type ProductListingResultWithExtensions = Schemas['ProductListingResult'] & {
    extensions?: {
        categoryTree?: {
            tree?: Record<string, CategoryItem>
        }
    }
}

const categoryTree = computed<Record<string, CategoryItem> | null>(
    () => (getCurrentListing.value as ProductListingResultWithExtensions)?.extensions?.categoryTree?.tree ?? null
)

// On a manufacturer page the aggregations contain only one manufacturer entity
const pageManufacturerId = computed<string | null>(() => {
    const aggregations = getCurrentListing.value?.aggregations as Record<string, { entities?: { id: string }[] }> | undefined
    const entities = aggregations?.manufacturer?.entities
    if (Array.isArray(entities) && entities.length === 1) return entities[0]?.id ?? null
    return null
})

// Shared filter state provided by the parent page/listing component
const selectedFilters = useListingFilters()

const searchCriteriaForRequest: ComputedRef<RequestParameters<"searchPage">> =
    computed(() => ({
        manufacturer: [...selectedFilters.manufacturer]?.join("|"),
        properties: [...selectedFilters.properties]?.join("|"),
        "min-price": Number(selectedFilters["min-price"]) || undefined,
        "max-price": Number(selectedFilters["max-price"]) || undefined,
        order: getCurrentSortingOrder.value,
        search: isSearchListing.value ? (route.query.search as string || '') : "",
        limit: route.query.limit ? Number(route.query.limit) : 40,
    }))


function buildQuery() {
    const query = { ...filtersToQuery(searchCriteriaForRequest.value) } as Record<string, unknown>

    if (isSearchListing.value) {
        query.search = route.query.search
    }

    const shouldUseIsNew = isNewPage.value || route.query.isNew === '1'
    if (shouldUseIsNew && selectedFilters.properties.has(IS_NEW_PROPERTY_ID)) {
        const remaining = [...selectedFilters.properties].filter(id => id !== IS_NEW_PROPERTY_ID)
        query.properties = remaining.length ? remaining.join('|') : undefined
        query.isNew = '1'
    } else {
        delete query.isNew
    }

    if (isOnSalePage.value) {
        query.isOnSale = '1'
    }

    return query
}

// Handle filter changes
const onOptionSelectToggle = async ({
    code,
    value,
}: {
    code: string;
    value: string;
}) => {
    if (!["properties", "manufacturer"].includes(code)) {
        selectedFilters[code] = value;
    } else {
        const filterSet = selectedFilters[code] as Set<string>;
        if (filterSet.has(value)) {
            filterSet.delete(value);
        } else {
            filterSet.add(value);
        }
    }
    await router.push({ query: buildQuery() } as RouteLocationRaw);
    await doSearch(withOnSalePostFilter({
        ...route.query,
        ...searchCriteriaForRequest.value,
        isNew: undefined,
    }));
};

// Clear all filters
const clearFilters = async () => {
    selectedFilters.manufacturer.clear();
    selectedFilters.properties.clear();
    selectedFilters["min-price"] = undefined;
    selectedFilters["max-price"] = undefined;
    await router.push({ query: buildQuery() } as RouteLocationRaw);
};

async function onResetFilters() {
    await clearFilters();
    await doSearch(withOnSalePostFilter({
        ...route.query,
        ...filtersToQuery(searchCriteriaForRequest.value),
        isNew: undefined,
    }));
}

// Clear filters for a specific category
const clearFilterCategory = async (filter: ListingFilter) => {
    if (!filter?.code) return

    if (filter.code === 'manufacturer' || filter.code === 'properties') {
        (selectedFilters[filter.code] as Set<string>).clear()
    } else {
        selectedFilters[filter.code] = undefined
    }

    await router.push({ query: buildQuery() } as RouteLocationRaw)

    await doSearch(withOnSalePostFilter({
        ...route.query,
        ...searchCriteriaForRequest.value,
        isNew: undefined,
    }))
}

// Computed properties for displaying current state
const selectedOptionIds = computed(() => [
    ...selectedFilters.properties,
    ...selectedFilters.manufacturer,
]);

const showResetFiltersButton = computed<boolean>(() =>
    selectedOptionIds.value.length !== 0 ||
    !!selectedFilters["max-price"] ||
    !!selectedFilters["min-price"]
);

const open = ref(false)

/*
* Filter Types
*/
const filterMap = (filterCode: string) => {
    const map: {
    [key: string]: object;
  } = {
      manufacturer: ProductListingFilterProperties,
      properties: ProductListingFilterProperties,
      price: ProductListingFilterPrice,
  };

    return map[filterCode];
};

const getStaticFilterName = (filterCode: string): string => {
    const map: {
        [key: string]: string;
    } = {
        manufacturer: t('listing.filter.manufacturerLabel'),
        price: t('listing.filter.priceLabel'),
    };

    return map[filterCode] ? map[filterCode] : '';
}

// Get count of selected filters for a specific filter category
const getSelectedFilterCount = (filter: ListingFilter): number => {
    if (!filter?.code) return 0

    // Manufacturer filter - count selected manufacturers
    if (filter.code === 'manufacturer') {
        return selectedFilters.manufacturer?.size || 0
    }

    // Properties filter - count selected properties for this specific property group
    if (filter.code === 'properties') {
        let count = 0
        for (const optionId of selectedFilters.properties) {
            const hasOption = filter?.options?.some(option => option?.id === optionId)
            if (hasOption) count++
        }
        return count
    }

    return 0
}

// Format filter label with selected count
const getFilterLabel = (filter: ListingFilter): string => {
    const isIsNewFilter = filter?.code === 'properties' &&
        filter?.options?.some((o: { id: string }) => o.id === IS_NEW_PROPERTY_ID)

    let baseName: string
    if (isIsNewFilter) {
        baseName = t('listing.filter.isNewLabel')
    } else if (filter?.code === 'properties') {
        const cf = (filter as unknown as { customFields?: Record<string, unknown> }).customFields
        baseName = (cf?.custom_property_group_set_display_name as string) || getTranslatedProperty(filter, 'name')
    } else {
        baseName = getStaticFilterName(filter?.code)
    }

    const count = getSelectedFilterCount(filter)

    return count > 0 ? `${baseName} (${count})` : baseName
}
</script>

<template>
    <div>
        <ClientOnly>
            <Teleport to="#TeleportFilter">
                <FoundationButton color="primary" class="w-full md:w-45 flex justify-between items-center" @click="open = true">
                    <FoundationIcon
                        name="filter"
                        class="block w-5 h-5"
                        size="md"
                        aria-hidden="true" />
                    <span class="block ps-5 mx-auto">{{ $t('listing.filter.title') }}</span>
                </FoundationButton>
            </Teleport>
        </ClientOnly>

        <SidenavOverlay 
            v-model="open" 
            class="bg-base-100" 
            direction="right"
            width-class="w-full md:w-[600px]"
        >
            <div class="relative flex items-center justify-center p-2 px-10 border-b border-border md:h-[100px] min-h-14">
                <FoundationHeadline tag="h2" class="text-base font-bold">{{ $t('listing.filter.title') }}</FoundationHeadline>
                <FoundationButton
                    ref="sidenavMenuCloseButton"
                    class="absolute left-3 md:left-6"
                    size="medium"
                    variant="ghost"
                    square
                    @click="open = false"
                >
                    <span class="sr-only">{{ $t('listing.filter.close') }}</span>
                    <FoundationIcon name="x" />
                </FoundationButton>
            </div>
            
            <ProductListingFilterCategories v-if="!isSearchListing" :category-tree="categoryTree" :manufacturer-id="pageManufacturerId" />

            <ComponentCollapsible
                v-if="getInitialFilters.length"
                id="filterContainer"
                :items="getInitialFilters"
                :show-more-text="$t('listing.filter.showMore')"
                :show-less-text="$t('listing.filter.showLess')"
                :is-desktop-mode="false"
                class="flex flex-col"
            >
                <template #default="{ shouldRenderItem, setItemRef }">
                    <!-- Visible filters -->
                    <template v-for="(filter, index) in getInitialFilters" :key="`${filter?.id || filter?.code}`">
                        <div
                            v-if="shouldRenderItem(filter) && !['tdxcats', 'streamCategories', 'rating', 'shipping-free'].includes(filter?.code)"
                            :ref="el => setItemRef(el as HTMLElement, index)"
                            class="relative"
                        >
                            <ComponentDropdown
                                :trigger-label="getFilterLabel(filter)"
                                class="w-full bg-base-200 border-0 border-b border-border btn-small justify-between font-bold px-4"
                                content-classes="bg-base-100 w-full px-4 py-0 z-50"
                            >
                                <div v-if="filter.code === 'manufacturer' || filter.code === 'properties'" class="flex justify-between items-center border-b border-border -mx-4 ps-4 py-1">
                                    <span>
                                        {{ getSelectedFilterCount(filter) }} {{ $t('listing.filter.selected') }}
                                    </span>

                                    <FoundationButton
                                        variant="ghost"
                                        class="font-bold underline text-sm"
                                        @click="clearFilterCategory(filter)"
                                    >
                                        {{ $t('listing.filter.resetCategory') }}
                                    </FoundationButton>
                                </div>
                                <component
                                    :is="filterMap(filter?.code)"
                                    :filter="filter"
                                    :selected-filters="selectedFilters"
                                    class="pt-4"
                                    @select-filter-value="onOptionSelectToggle"
                                />
                            </ComponentDropdown>
                        </div>
                    </template>
                </template>

                <!-- Custom toggle button slot implementation -->
                <template #toggle-button="{ toggle, isExpanded, hasOverflow }">
                    <FoundationButton
                        v-if="hasOverflow || (isExpanded && !hasOverflow)"
                        color="secondary"
                        variant="outline"
                        size="small"
                        :class="[
                            isExpanded ? 'relative' : 'absolute right-0'
                        ]"
                        :aria-expanded="isExpanded"
                        aria-controls="filterContainer"
                        @click="toggle"
                    >
                        {{ isExpanded ? $t('listing.filter.showLess') : $t('listing.filter.showMore') }}
                        <FoundationIcon :name="hasOverflow || (isExpanded && hasOverflow) ? 'chevron-down' : 'chevron-up'" class="ml-1" />
                    </FoundationButton>
                </template>
            </ComponentCollapsible>

            <!-- Reset filters button -->
            <div v-if="showResetFiltersButton" class="px-6 pt-4 -mx-2 mt-20 border-t border-border">
                <FoundationButton 
                    color="secondary"
                    size="small"
                    class="w-full"
                    @click="onResetFilters()"
                >
                    {{ $t('listing.filter.reset') }}
                </FoundationButton>
            </div>
        </SidenavOverlay>
    </div>
</template>
